/**
 * feat-138 — Pairing Challenge admin editor service client.
 *
 * Talks to the two admin endpoints introduced by ADR-0001 (`decisions/0001-
 * derivation-contract.md`):
 *   - GET  /admin/pairings/{pairing}          → the ONE logical head-to-head model (D1/D4)
 *   - PUT  /admin/pairings/{pairing}          → save the logical model (backend derives both rows, D2/D7)
 *   - GET  /admin/pairings/{pairing}/history  → audit snapshots (who/when/blob, D7)
 *
 * The editor edits ONE logical model; the BACKEND derives the two `challenge`
 * rows (the cross-app contract). The frontend NEVER derives for the wire — it
 * derives only client-side for the live PREVIEW (see pairingDerivation.ts).
 *
 * Bearer attached per-call from `getAuthToken()`, same pattern as
 * `fetchChallengesForLocation` in RestaurantBackend.ts. Env-driven target via
 * `config.restaurantBackendHost` — STAGING for build/QA, per the feature spec.
 */
import { apiCall } from '../components/restaurant/RestaurantBackend';
import { getAuthToken } from './authToken';

// ── Fixed server constants (ADR D2/D6) — the client never picks these ─────────
// The single pilot pairing. Same fixed ids in every env; the env picks the DB.
export const PAIRING_SLUG = 'ladle-leaf-one-vs-580';
export const ONE_CALIFORNIA_LOCATION_ID = '841841f8-e2ec-4057-9328-9ef715099da0';
export const FIVE_EIGHTY_LOCATION_ID = 'd3c4378c-c7e3-4762-a954-c0910db6a5c9';

// ── The logical model (ADR D1) — FE⇄BE contract ──────────────────────────────
export type PaceStatus = 'ahead' | 'behind' | 'on_pace';

export interface PairingTeamLogical {
  location_id: string;
  name: string;
  percent_to_goal: number;
  pace_status: PaceStatus;
  pace_label: string;
  bar_color: string;
  avatar_url: string;
  avatar_placeholder: string;
  // THIS location's own header numbers (the only genuinely per-location values).
  yesterday_count: number;
  today_goal: number;
  // feat-140 — human-set 6-week goal (carry-forward); denominator for percent_to_goal.
  final_goal: number;
  // feat-140 — job-computed campaign-to-date 5-key offer count (read-only sanity display,
  // round-trips through the editor unchanged). Numerator behind percent_to_goal.
  cumulative_offers: number;
}

export interface PairingLogical {
  pairing: string;
  title: string;
  campaign: { start: string; end: string };
  bars_title: string;
  pace_percent: number;
  today_goal_caption: string;
  // EXACTLY 2, canonical fixed order: One California, then 580 California (D1).
  teams: PairingTeamLogical[];
}

// ── History snapshot (ADR D7) ─────────────────────────────────────────────────
export interface PairingHistoryEntry {
  id?: number | string;
  triggered_by: string;
  run_at: string;
  challenge_id?: string;
  // The exact derived blob persisted for that challenge, as stored.
  status_json: unknown;
}

// Backend endpoints live on the challenges_router mounted at prefix="/api"
// (confirmed with feat-138 backend) — so the real paths carry the /api prefix.
// apiCall() uses the path verbatim; match `fetchChallengesForLocation`.
const pairingPath = (pairing: string = PAIRING_SLUG) =>
  `/api/admin/pairings/${encodeURIComponent(pairing)}`;

const authHeaders = (options: RequestInit = {}): Headers => {
  // Per-call bearer attachment (same narrow-blast pattern as feat-007 /
  // fetchChallengesForLocation) — the shared apiCall keeps the token fresh.
  const token = getAuthToken();
  const headers = new Headers(options.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
};

/**
 * Hydrate the editor form from the two current rows (backend de-dups to one
 * logical model, D4). Returns null on 404 (the pairing isn't seeded in this
 * env) so the caller can render an uninitialized state instead of throwing.
 */
export const fetchPairing = async (
  pairing: string = PAIRING_SLUG,
): Promise<PairingLogical | null> => {
  const headers = authHeaders();
  const response = await apiCall(pairingPath(pairing), { method: 'GET', headers });
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Failed to load pairing: ${response.status}`);
  }
  return (await response.json()) as PairingLogical;
};

/**
 * Save the logical model. The backend derives BOTH blobs, writes both rows in
 * one transaction, and appends a history snapshot (D2/D7). Returns the freshly
 * hydrated model when the backend echoes it back; otherwise the payload sent.
 */
export const savePairing = async (
  logical: PairingLogical,
): Promise<PairingLogical> => {
  const headers = authHeaders();
  headers.set('Content-Type', 'application/json');
  const response = await apiCall(pairingPath(logical.pairing), {
    method: 'PUT',
    headers,
    body: JSON.stringify(logical),
  });
  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = typeof body?.detail === 'string' ? body.detail : JSON.stringify(body?.detail ?? body);
    } catch {
      /* non-JSON error body */
    }
    throw new Error(`Save failed (${response.status})${detail ? `: ${detail}` : ''}`);
  }
  // Backend may 200 with the re-hydrated model (round-trip, D8) or 204.
  try {
    const body = (await response.json()) as PairingLogical;
    if (body && Array.isArray(body.teams)) return body;
  } catch {
    /* empty/204 body — fall through */
  }
  return logical;
};

/**
 * Load audit history (who/when/full-blob, D7), most-recent first. Tolerant of
 * either a bare array or a `{ history: [...] }` envelope, and degrades to an
 * empty list on 404/error so the panel never breaks the page.
 */
export const fetchPairingHistory = async (
  pairing: string = PAIRING_SLUG,
): Promise<PairingHistoryEntry[]> => {
  const headers = authHeaders();
  const response = await apiCall(`${pairingPath(pairing)}/history`, { method: 'GET', headers });
  if (!response.ok) return [];
  try {
    const body = await response.json();
    const list = Array.isArray(body) ? body : Array.isArray(body?.history) ? body.history : [];
    return list as PairingHistoryEntry[];
  } catch {
    return [];
  }
};
